import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { Button } from '../../components/button'
import { formatCurrency } from '../../helpers'
import {
  useRemoveItemFromCartMutation,
  useUpdateCartItemQuantityMutation,
} from '../../redux-toolkits/orders/orders.slice'
import { CartComboItem } from '../../redux-toolkits/orders/orders.type'
import { ApiCartItem } from '../../redux-toolkits/cart/cart.type'
import { VariantCombination } from '../../redux-toolkits/products/products.type'
export const CartSummaryItem = ({
  cartItem,
  matchingProductVariantCombination,
}: {
  cartItem: ApiCartItem
  matchingProductVariantCombination?: VariantCombination
}) => {
  const [cartItemQuantity, setCartItemQuantity] = useState<number>(
    cartItem.quantity,
  )
  const [editingQuantity, setEditingQuantity] = useState<boolean>(false)

  const [updateCartItemQuantity, { isLoading: updatingCartItemQuantity }] =
    useUpdateCartItemQuantityMutation()

  const [removeItemFromCart, { isLoading: removingItemFromCart }] =
    useRemoveItemFromCartMutation()

  useEffect(() => {
    setCartItemQuantity(cartItem.quantity)
  }, [cartItem])

  return (
    <div className="border-b py-6 first:pt-0 text-sm flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-4 justify-between flex-wrap">
          <p className="text-black font-medium">{cartItem.product.name}</p>

          <div className="flex items-center gap-4">
            <Button
              type="button"
              onClick={() => {
                setCartItemQuantity(cartItemQuantity - 1)
              }}
              className={classNames(
                'disabled:opacity-20 disabled:cursor-not-allowed !w-fit !px-0 !py-0',
                {
                  hidden: !editingQuantity,
                },
              )}
              disabled={cartItemQuantity <= 1 || updatingCartItemQuantity}
              loading={false}
              primary={false}
              variant="plain"
              icon="minus-circle"
              iconSize={24}
            />

            <p>{cartItemQuantity}</p>

            <Button
              type="button"
              onClick={() => {
                setCartItemQuantity(cartItemQuantity + 1)
              }}
              className={classNames(
                'disabled:opacity-20 disabled:cursor-not-allowed !w-fit !px-0 !py-0',
                {
                  hidden: !editingQuantity,
                },
              )}
              disabled={
                (cartItem.product.isVariant
                  ? cartItemQuantity >=
                    (matchingProductVariantCombination?.inventoryCount ?? 0)
                  : cartItemQuantity >= cartItem.product.inStockQuantity) ||
                updatingCartItemQuantity
              }
              loading={false}
              primary={false}
              variant="plain"
              icon="plus-circle"
              iconSize={24}
            />

            <Button
              type="button"
              onClick={() => {
                setEditingQuantity(true)
              }}
              className={classNames('!w-fit !px-0 !py-0', {
                hidden: editingQuantity,
              })}
              disabled={false}
              loading={updatingCartItemQuantity}
              primary={false}
              variant="plain"
              icon="edit"
              iconSize={20}
            />
          </div>
        </div>

        <div className="w-full flex items-center gap-4">
          <p className="text-sec-black mt-1">
            {formatCurrency(cartItem.price, {
              currency: 'NGN',
              decimalPlaces: 2,
            })}
          </p>

          <Button
            type="button"
            onClick={async () => {
              setCartItemQuantity(cartItem.quantity)
              setEditingQuantity(false)
            }}
            className={classNames('ml-auto !w-fit !px-0 !py-0 text-danger', {
              hidden: !editingQuantity,
            })}
            disabled={updatingCartItemQuantity}
            loading={false}
            primary={false}
            variant="plain"
            icon="close-circle-filled"
            iconSize={24}
          />

          <Button
            type="button"
            onClick={async () => {
              const resp = await updateCartItemQuantity({
                id: cartItem.cartId,
                payload: {
                  productId: cartItem.product._id,
                  quantity: cartItemQuantity,
                  cartItemId: cartItem._id,
                },
              })

              if (resp.data?.success) {
                setEditingQuantity(false)
              }
            }}
            className={classNames('!w-fit !px-0 !py-0', {
              hidden: !editingQuantity,
            })}
            disabled={updatingCartItemQuantity}
            loading={updatingCartItemQuantity}
            primary={false}
            variant="plain"
            icon="check-icon"
            iconSize={24}
          />
        </div>
      </div>

      {/* Combo  */}
      {cartItem?.comboItems?.length > 0 ? (
        <div className="text-sm flex flex-col gap-4">
          {Object.entries(
            cartItem?.comboItems?.reduce?.(
              (acc, item) => {
                if (!acc[item.groupName]) {
                  acc[item.groupName] = []
                }
                acc[item.groupName].push(item)
                return acc
              },
              {} as Record<string, CartComboItem[]>,
            ),
          )?.map(([name, items]) => {
            return (
              <div key={name} className="flex flex-col gap-1">
                <p className="text-black font-medium">{name}:</p>
                {items.map((item, idx) => {
                  return (
                    <p
                      key={`Extra-option-${idx}`}
                      className="text-sec-black flex items-center gap-2 justify-between"
                    >
                      {item.name} (Qty: {item.quantity})
                    </p>
                  )
                })}
              </div>
            )
          })}
        </div>
      ) : undefined}

      {/* Variants  */}
      {cartItem.variants
        ? cartItem.variants.map((v, idx) => {
            return (
              <div key={`Extra-option-${idx}`} className="flex flex-col gap-1">
                <p className="text-black font-medium">{v.variantId.name}</p>

                <div className="text-sec-black flex items-center gap-2 justify-between">
                  <p>{v.value}</p>
                </div>
              </div>
            )
          })
        : undefined}

      <Button
        variant="plain-danger"
        label="Remove"
        loading={removingItemFromCart}
        onClick={async () => {
          await removeItemFromCart({
            id: cartItem.cartId,
            payload: {
              productId: cartItem.product._id,
              cartItemId: cartItem._id,
            },
          })
        }}
        type="button"
        icon="bin"
        className="!px-0 !py-0 !border-0 !w-fit ml-auto"
        iconSize={20}
      />
    </div>
  )
}
